import React, { useState, useEffect } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { Users, X, Loader2, Search, Phone } from 'lucide-react';
import api from '../../api/auth.api';

interface SubcontractorType {
  type_id: number;
  type_name: string;
  type_code: string;
  show_in_daily_reports: boolean;
}

interface JobAssignment {
  id: number;
  type_id: number;
  subcontractor_company: string;
  contact_name: string;
  contact_phone: string;
}

interface SubcontractorDisplay {
  type_id: number;
  type_name: string;
  type_code: string;
  company: string | null;
  contact: string | null;
  phone: string | null;
}

// Get passed jobId from navigation
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};

// Format phone number
const formatPhone = (phone: string | null) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

export const SubcontractorViewer: React.FC = () => {
  const passedJobId = usePassedJobId();

  const [subcontractors, setSubcontractors] = useState<SubcontractorDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobInfo, setJobInfo] = useState<{ job_name: string; bradanna_job_number: string } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!passedJobId) {
        setError('No job selected');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch all three in parallel
        const [typesRes, assignmentsRes, jobRes] = await Promise.all([
          api.get('/api/subcontractors/types'),
          api.get(`/api/jobs/${passedJobId}/subcontractors`),
          api.get(`/api/jobs/${passedJobId}`)
        ]);

        // Job info
        if (jobRes.data.success && jobRes.data.data) {
          const job = jobRes.data.data;
          setJobInfo({
            job_name: job.job_name,
            bradanna_job_number: job.bradanna_job_number
          });
        }

        // Merge types with assignments and filter
        if (typesRes.data.success && assignmentsRes.data.success) {
          const types: SubcontractorType[] = typesRes.data.data;
          const assignments: JobAssignment[] = assignmentsRes.data.data;

          const displayList = types.map(type => {
            const assignment = assignments.find((a) => a.type_id === type.type_id);
            return {
              type_id: type.type_id,
              type_name: type.type_name,
              type_code: type.type_code,
              company: assignment?.subcontractor_company || null,
              contact: assignment?.contact_name || null,
              phone: assignment?.contact_phone || null
            };
          }).filter(sub => {
            // 1. Hide types not enabled for daily reports
            if (types.find(t => t.type_id === sub.type_id)?.show_in_daily_reports === false) return false;

            // 2. Hide if not assigned (no company, empty, or 'Not Required')
            if (!sub.company || sub.company === '' || sub.company === 'Not Required') return false;

            // 3. Hide 'By General Contractor/Owner' EXCEPT for testing & survey
            if (sub.company === 'By General Contractor/Owner') {
              if (sub.type_code === 'testing' || sub.type_code === 'survey') return true;
              return false;
            }

            return true;
          });

          setSubcontractors(displayList);
        }
      } catch (err) {
        console.error('Error fetching subcontractors:', err);
        setError('Failed to load subcontractors');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [passedJobId]);

  // Filter by search
  const filteredSubcontractors = subcontractors.filter(sub => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      sub.type_name.toLowerCase().includes(search) ||
      (sub.company && sub.company.toLowerCase().includes(search)) ||
      (sub.contact && sub.contact.toLowerCase().includes(search))
    );
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Loader2 size={40} style={{ color: '#14b8a6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', marginTop: '16px' }}>Loading subcontractors...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <h1 style={{ color: '#1e293b', fontSize: '20px', fontWeight: '700', margin: 0 }}>
          Subcontractors
        </h1>
        {jobInfo && (
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>
            {jobInfo.job_name} • #{jobInfo.bradanna_job_number}
          </p>
        )}
      </div>

      <div style={{ padding: '16px' }}>
        {/* Count badge */}
        <div style={{
          backgroundColor: '#14b8a6',
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: 'white' }}>{subcontractors.length}</div>
          <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>Assigned Subcontractors</div>
        </div>

        {/* Search */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <Search size={20} color="#64748b" />
          <input
            type="text"
            placeholder="Search by type, company, or contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              color: '#1e293b',
              backgroundColor: 'transparent'
            }}
          />
          {searchTerm && (
            <X
              size={20}
              color="#94a3b8"
              onClick={() => setSearchTerm('')}
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>

        {/* Subcontractor Cards */}
        {filteredSubcontractors.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Users size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
            <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>
              {searchTerm ? 'No subcontractors found' : 'No subcontractors assigned'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredSubcontractors.map(sub => (
              <div
                key={sub.type_id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                {/* Type badge */}
                <span style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  backgroundColor: '#f0fdfa',
                  color: '#0d9488',
                  fontSize: '11px',
                  fontWeight: '600',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  {sub.type_name}
                </span>

                {/* Company */}
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                  {sub.company}
                </div>

                {/* Contact + Phone */}
                {(sub.contact || sub.phone) && (
                  <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {sub.contact && (
                      <span style={{ fontSize: '14px', color: '#64748b' }}>
                        {sub.contact}
                      </span>
                    )}
                    {sub.contact && sub.phone && (
                      <span style={{ color: '#cbd5e1' }}>•</span>
                    )}
                    {sub.phone && (
                      <a
                        href={`tel:${sub.phone}`}
                        style={{
                          fontSize: '14px',
                          color: '#2563eb',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Phone size={13} />
                        {formatPhone(sub.phone)}
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
